import { logger } from '@adornis/base/logging.js';
import type { Constructor, Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { Materialize } from '@adornis/materialized-views/decorators/materialize.js';
import { BasicProduct } from '@adornis/payments/db/basic-product.js';
import { Price } from '@adornis/payments/db/prices.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { JSDOM } from 'jsdom';
import { DateTime } from 'luxon';
import { ParticipationState, ProductRuntimeState } from '../enums/enums.js';
import { isDefined } from '../util.js';
import type { Category } from './category.js';
import type { Institution } from './institution.js';
import { Participation } from './Participation.js';
import { ProductEntryRequirement } from './ProductEntryRequirement.js';
import { Setting } from './settings.js';
import { TextMultipleLangItem } from './TextMultipleLangItem.js';
import type { User } from './user.js';

/**
 * Processes an array of DOM elements to extract readable text,
 * handling specific structures like bullet lists.
 * @param {Array<Element>} elements - An array of DOM elements to process.
 * @returns {string} - The extracted readable text, joined by newlines.
 */
function processElements(elements) {
  const lines = [] as string[];

  // Iterate through the direct children elements
  for (const element of elements) {
    // Ignore <style> tags
    if (element.tagName.toLowerCase() === 'style') {
      continue;
    }

    // Check if it's a bullet list item container
    if (element.classList.contains('prosemirror-flat-list') && element.dataset.listKind === 'bullet') {
      // Find the actual text content within the list item
      const textElement = element.querySelector('.list-content tr-text span');
      if (textElement) {
        const text = textElement.textContent.trim();
        if (text) {
          lines.push(`  • ${text}`); // Prepend bullet point marker
        }
      }
    }
    // Check if it's a regular text block container
    else if (element.tagName.toLowerCase() === 'tr-text') {
      const textElement = element.querySelector('span');
      if (textElement) {
        const text = textElement.textContent.trim();
        if (text) {
          lines.push(text);
        }
      } else {
        const text = element.textContent.trim();
        if (text) {
          lines.push(text);
        }
      }
    }
    // You could add more 'else if' conditions here for other element types
    // like ordered lists (data-list-kind="ordered") if needed.
    // else {
    //   console.log("Ignoring unexpected element:", element.tagName);
    // }
  }

  // Join the processed lines with newline characters
  return lines.join('\n');
}

/**
 * Transforms an HTML string (potentially containing specific ProseMirror structures)
 * into readable plain text using jsdom.
 * @param {string} htmlString - The HTML content as a string.
 * @returns {string} - The extracted readable text.
 */
export function buildifyHtmlToReadableText(htmlString) {
  // 1. Use JSDOM to parse the HTML string
  const dom = new JSDOM(htmlString);
  // 2. Get the document object from the JSDOM instance
  const doc = dom.window.document;

  // 3. Find the main content container (<node-doc>)
  //    We query within the parsed jsdom document
  const nodeDoc = doc.querySelector('node-doc');

  if (!nodeDoc) {
    console.error('Warning: Could not find the <node-doc> element. Trying to process body children directly.');
    // Fallback: Try processing direct children of body if <node-doc> is missing
    // This handles cases where only the *content* of node-doc might be provided
    const bodyChildren = Array.from(doc.body.children);
    if (bodyChildren.some(el => el.tagName.toLowerCase() !== 'node-doc' && el.tagName.toLowerCase() !== 'style')) {
      return processElements(bodyChildren); // Process body children directly
    }
    return ''; // Return empty if nothing relevant found
  }

  // 4. Process the children of the <node-doc> element using the helper function
  return processElements(Array.from(nodeDoc.children));
}

export function htmlToRawTemplaterText(quellcode?: string) {
  console.log({ quellcode });
  if (!quellcode) return '';
  const result = quellcode
    .replace(/\<style[^>]*>[\s\S]*?<\/style>/, '')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/\<br[^>]*(>|$)/, '\n')
    .replace(/<\/[^>]+text(>|$)/g, '\n')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/&[a-z]+;/g, '');
  return result;
}

export type VersionChangeValues = { old: any; new: any };
export type VersionChangesList = Array<VersionChangeValues>;
export type VersionChangeType = VersionChangeValues | VersionChangesList;
export type VersionChangesAttrMap = Record<string, VersionChangeType>;

@Entity()
export class ProductFilter extends AdornisFilter {
  static override _class = 'ProductFilter';

  constructor(doc: PartialEntityData<ProductFilter>) {
    super(doc);
  }

  @Field(type => [String]) institutionIDs: Maybe<string[]>;
  @Field(type => [String]) languages: Maybe<string[]>;
  @Field(type => [String]) topics: Maybe<string[]>;
  @Field(type => [String]) presences: Maybe<string[]>;
  @Field(type => [String]) timeframeIDs: Maybe<string[]>;
  @Field(type => [String]) categoryIDs: Maybe<string[]>;
  @Field(type => String) description: Maybe<string>;
  @Field(type => Boolean) hasFreeSeats: Maybe<boolean>;

  toQueryParmsItems(prefix: string) {
    const items = [] as [string, Maybe<string>][];

    function pushAll(key: string, values: Maybe<string[]>) {
      if (!values || values.length === 0) items.push([key + '[]', null]);
      else items.push([key + '[]', values.join(';')]);
    }

    pushAll('institutionIDs', this.institutionIDs);
    pushAll('languages', this.languages);
    pushAll('topics', this.topics);
    pushAll('presences', this.presences);
    pushAll('timeframeIDs', this.timeframeIDs);
    pushAll('categoryIDs', this.categoryIDs);
    items.push(['description', this.description ? this.description : null]);
    items.push(['hasFreeSeats', this.hasFreeSeats ? 'true' : null]);

    return items.map(([key, value]) => [prefix + key, value]);
  }

  static fromQueryParamItems(prefix: string, items: [string, string][]) {
    const data = {} as any;
    for (const key of Object.keys(ProductFilter.allFields)) {
      const [prm_key, value] = items.find(
        ([itmKey]) => `${prefix}${key}` === itmKey || `${prefix}${key}[]` === itmKey,
      ) ?? [null, null];
      if (value && prm_key?.endsWith('[]')) data[key] = value.split(';');
      else if (value) data[key] = value;
    }
    return new ProductFilter(data);
  }
}

@Entity()
export class CertificateAttachedProduct extends AdornisEntity {
  static override _class = 'CertificateAttachedProduct';

  constructor(doc: PartialEntityData<CertificateAttachedProduct>) {
    super(doc);
  }

  @Field(type => String)
  certificateID: Maybe<string>;

  @Field(type => [TextMultipleLangItem])
  descriptionsHTML!: TextMultipleLangItem[];

  @Field(type => DateTime)
  exportDate?: Maybe<DateTime>;
}

(async () => {
  if (!context?.serverContext) return;
  const productCollection = await getRawCollection('products');
  await productCollection.createIndex({ institutionID: 1 });
})();

(async () => {
  if (!context?.serverContext) return;
  const collection = await getRawCollection('products-materialized');
  await collection.createIndex({ isActive: 1 });
})();

@Materialize({
  materializedCollectionName: 'products-materialized',
  selectionSet: async () => {
    const { Suspension } = await import('./suspension.js');
    const { UserStammdaten } = await import('./stammdaten.js');
    const { Appointment } = await import('./appointment.js');

    return {
      name: 1,
      prices: { unitAmount: 1 },
      sortingWeight: 1,
      hasPrice: 1,
      categoryID: 1,
      category: { name: 1, sortingWeight: 1 },
      institutionID: 1,
      timeframeID: 1,
      timeframe: { name: 1 },
      productCopyID: 1,
      isLDAPOnly: 1,
      publishDate: 1,
      topic: 1,
      language: 1,
      presenceType: 1,
      isCanceled: 1,
      capacity: 1,
      leftCapacity: 1,
      participationCount: 1,
      highPriorityParticipationCount: 1,
      hideCapacity: 1,
      isActive: 1,
      startDate: 1,
      appointments: selectionSet(() => Appointment, 1),
      workshopSetName: 1,
      slots: { _id: 1, beraterID: 1, isBooked: 1, start: 1, end: 1, isNonBlocking: 1 },
      slotConfig: {
        _id: 1,
        _class: 1,
        start: 1,
        end: 1,
        hasLunchBreak: 1,
        startLunch: 1,
        endLunch: 1,
        slotDuration: 1,
      },
      suspensions: selectionSet(() => Suspension, 1),
      userStammdaten: selectionSet(() => UserStammdaten, 1),
      translatedDescriptionHTML: selectionSet(() => TextMultipleLangItem, 1),
      translatedShortDescriptionHTML: selectionSet(() => TextMultipleLangItem, 1),
      targetGroup: 1,
      location: 1,
      maxParticipantsCount: 1,
      minParticipantsCount: 1,
      waitinglistEnabled: 1,
      waitingParticipantsCount: 1,
      affiliation: { affiliation: 1, affiliationID: 1 },
      lecturer: 1,
      createdAt: 1,
      internalComments: 1,
      kostenart: 1,
      nachrueckBlockTimeForLowPriorityInDays: 1,
      settings: selectionSet(() => Setting, 3),
      productEntryRequirementLabels: 1,
      runtimeStatus: 1,
    };
  },
})
@Entity()
export class Product extends BasicProduct {
  static override _class = 'Product';
  static override _collectionName = 'products';

  @validate(nonOptional())
  @Field(type => String)
  override name!: string;

  get getLastExportDate() {
    return (this.attachedCertificate?.exportDate ?? DateTime.now()).toFormat('dd.MM.yyyy');
  }

  @validate(async options => {
    const descriptions = (options.value ?? []) as TextMultipleLangItem[];

    if (!descriptions.length)
      throw new ValidationError('Beschreibung ist ein Pflichtfeld', {
        translationKey: 'validation_non_optional',
        key: options.key,
      });
  })
  @Field(type => [TextMultipleLangItem])
  translatedDescriptionHTML!: TextMultipleLangItem[];

  @Field(type => Float)
  sortingWeight?: number;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: v => v ?? false })
  hasPrice!: boolean;

  @validate(nonOptional())
  @Field(type => [Price], { default: v => v ?? [new Price({ unitAmount: 0, tax_behavior: 'inclusive' })] })
  override readonly prices!: Price[];

  @validate(nonOptional())
  @Field(type => String)
  categoryID!: string;

  @DataLoadedField(type => require('./category.js').Category, ({ categoryID }) => categoryID)
  category: Maybe<Category>;

  @validate(nonOptional())
  @Field(type => String)
  institutionID!: string;

  @DataLoadedField(type => require('./institution.js').Institution, ({ institutionID }) => institutionID)
  institution: Maybe<Institution>;

  @Field(type => String)
  timeframeID: Maybe<string>;

  @DataLoadedField(type => Timeframe, ({ timeframeID }) => timeframeID)
  timeframe: Maybe<Timeframe>;

  @Field(type => CertificateAttachedProduct)
  attachedCertificate: Maybe<CertificateAttachedProduct>;

  // This field depends on time, therefore it should not be part of the materialized view.
  // But it is really helpfull that it is.
  // Therefor the view gets refreshed periodically. The code for that is in email-scheduling
  @Dependencies({
    classes: () => [Timeframe],
    ids: async function (this: PartialEntityData<Product>) {
      return this.timeframeID ? [this.timeframeID] : [];
    },
  })
  @Field(type => Boolean, {
    resolve: function (this: Product) {
      return async () => {
        if (typeof this.isActive === 'boolean') return this.isActive;
        const product = await getByID(
          Product,
          this._id,
        )({
          _id: 1,
          _class: 1,
          isCanceled: 1,
          institution: {
            maintenanceMode: 1,
          },
          publishDate: 1,
          timeframe: {
            isActive: 1,
            publishDate: 1,
            till: 1,
          },
          appointments: { start: 1 },
          slotConfig: {
            start: 1,
          },
        });

        if (!product) throw new Error('product not found');

        const m = await import('../server/logic/Product.js');
        return m.isProductActive(product);
      };
    },
  })
  isActive: Maybe<boolean>;

  @Dependencies({
    classes: () => [Participation],
    ids: async function (this: PartialEntityData<Product>) {
      const participations = await loadMongoData(Participation, { productID: this._id }, { _id: 1 });

      if (!participations) return [];
      return participations.map(p => p._id);
    },
  })
  @Field(type => Int, {
    resolve: function (this: Product) {
      return async () => {
        const c = await getRawCollection(Participation._collectionName); // ignore publish filter
        const participationCount = await c.countDocuments({
          productID: this._id,
          state: { $in: [ParticipationState.IN, ParticipationState.PARTICIPATED] },
        });

        return participationCount;
      };
    },
  })
  participationCount!: number;

  @Dependencies({
    classes: () => [ProductEntryRequirement],
    ids: async function (this: PartialEntityData<Product>) {
      const entryRequirements = await loadMongoData(ProductEntryRequirement, { productID: this._id }, { _id: 1 });
      if (!entryRequirements) return [];
      return entryRequirements.map(p => p._id);
    },
  })
  @Field(type => [[String, String]], {
    resolve: function (this: Product) {
      return async () => {
        if (this.productEntryRequirementLabels) return this.productEntryRequirementLabels;
        const requirements = await loadMongoData(ProductEntryRequirement, { productID: this._id });
        const labelPromises = [] as Promise<[string, string]>[];
        for (const requirement of requirements) {
          requirement.product = this;
          const node = baseqlMetaData.entities.find(
            node => (node.type() as typeof AdornisEntity)._class === requirement._class,
          );
          const entity = node?.type() as Constructor<any>;
          const req = new entity(requirement);
          labelPromises.push(req.label());
        }
        const labels = Promise.all(labelPromises);
        return labels;
      };
    },
  })
  productEntryRequirementLabels!: [string, string][];

  @Dependencies({
    classes: () => [Setting],
    ids: async function (this: PartialEntityData<Product>) {
      const settings = await loadMongoData(Setting, { productID: this._id }, { _id: 1 });
      if (!settings) return [];
      return settings.map(p => p._id);
    },
  })
  @Field(type => Setting, {
    resolve: function (this: Product) {
      return async () => {
        const result = (await loadMongoData(Setting, { productID: this._id })) as Setting[];
        return result[0] ?? new Setting({ productID: this._id, parentInstitutionID: this.institutionID });
      };
    },
  })
  settings!: Setting;

  @Field(type => DateTime)
  lastReminderMailSent?: DateTime;

  @Field(type => DateTime)
  lastMeetingInfoMailSent?: DateTime;

  @Field(type => String)
  productCopyID: Maybe<string>;

  @Field(type => Boolean, { default: v => v ?? false })
  isLDAPOnly: Maybe<boolean>;

  @Field(type => DateTime)
  publishDate: Maybe<DateTime>;

  @Field(type => String)
  topic: Maybe<string>;

  @Field(type => String)
  language: Maybe<'de' | 'en'>;

  @Field(type => String)
  presenceType: Maybe<'not_selected' | 'in_person' | 'online' | 'hybrid'>; // null, undefined should be treated as 'not_selected'

  @Field(type => Boolean, { default: v => v ?? false })
  isCanceled: Maybe<boolean>;

  @Field(type => Boolean, { default: v => v ?? false })
  overrideInstitutionWaitinglistPriorityRules: Maybe<boolean>;

  @Dependencies({
    classes: () => [Product],
    ids: async function (this: PartialEntityData<Product>) {
      return [this._id!];
    },
  })
  @Field(type => ProductRuntimeState, {
    resolve: function (this: Product) {
      return async () => {
        // If already set, return the value
        if (this.runtimeStatus) return this.runtimeStatus;

        // Get raw product data from database - using a simpler approach to avoid type errors
        const productCollection = await getRawCollection(Product._collectionName);
        const productData = await productCollection.findOne({ _id: this._id });

        if (!productData) throw new Error('product data not found');

        const now = new Date();

        // Get the earliest start date and latest end date from appointments or slots
        let earliestStartDate = productData.appointments?.[0]?.start ?? productData.slotConfig?.start;
        let latestEndDate = productData.appointments?.[0]?.end ?? productData.slotConfig?.end;

        if (!earliestStartDate) return ProductRuntimeState.RUNNING; // If we don't have dates, default to RUNNING
        if (earliestStartDate > now) return ProductRuntimeState.PLANNED; // Determine status based on dates
        if (latestEndDate && latestEndDate < now) return ProductRuntimeState.COMPLETED;
        return ProductRuntimeState.RUNNING;
      };
    },
  })
  runtimeStatus!: ProductRuntimeState;

  get unitAmount() {
    if (!this.hasPrice) return 0;
    return this.prices[0]?.unitAmount ?? 0;
  }

  get isPaymentNeeded() {
    if (!this.hasPrice) return false;
    const prices = this.prices;
    if (!prices || !prices[0]) return false;
    const price = prices[0];
    if (!price) return false;
    if (price.unitAmount > 0) return true;
    return false;
  }

  static override async beforeRemove(id: string, entityData: Product): Promise<void> {
    await super.beforeRemove(id, entityData);

    const { Participation } = await import('./Participation.js');
    const collectionParticipation = await getRawCollection<Participation>(Participation._collectionName);

    // send emails to participants
    const { notifyProductDeletion } = await import('../notification/product-loeschung.js');
    const product = await getByID(
      Product,
      entityData._id,
    )({
      ...selectionSet(() => Product, 3),
      appointments: {
        _id: 1,
        start: 1,
        end: 1,
        name: 1,
        meetingLink: { link: 1 },
        location: 1,
      },
    });

    if (!product) throw new Error('product not found');
    await notifyProductDeletion(entityData._id, product);

    // remove participations
    await collectionParticipation.deleteMany({ productID: entityData._id });
  }

  override async save() {
    if (!this._id) throw new Error('Use this.create() to create new entities, .save() is only for updating data.');

    const result = await super.save();

    // avoid circular dependencies
    const { Bundle } = await import('./Bundle.js');
    await Bundle.updateForProduct(this._id)();

    return result;
  }

  // server side only functions start
  // --------------------------------------------------------------

  static async getParticipantsForProduct(productID: string) {
    const { Participation } = await import('./Participation.js');
    const { User } = await import('./user.js');
    const collectionParticipation = await getRawCollection<Participation>(Participation._collectionName);
    const participations = await collectionParticipation.find<Participation>({ productID }).toArray();
    const userCollection = await getRawCollection<User>(User._collectionName);
    const result = await Promise.all(
      participations.map(async participation => {
        const user = await userCollection.findOne<User>({ _id: participation.userID });
        if (!user) {
          logger.warn('User(' + participation.userID + ') not found for participation=' + participation._id);
          return undefined;
        }
        return { participation, user };
      }),
    );

    return result.filter(isDefined);
  }

  protected static async _getProductByID(productID: string): Promise<Maybe<Product>> {
    const productCollection = await getRawCollection<Product>(Product._collectionName);
    const product = await productCollection.findOne<Product>({ _id: productID });
    return constructValue(product);
  }
  // --------------------------------------------------------------
  // server side only functions end

  @Query(type => Int)
  static countMatchingProducts(
    @Arg('institutionID', type => String) institutionID?: string,
    @Arg('timeframeID', type => ID) timeframeID?: string,
  ) {
    return async () => {
      const collection = await getCollection<Product>(this._class);
      const filter = {} as any;
      if (institutionID) filter.institutionID = institutionID;
      if (timeframeID) filter.timeframeID = timeframeID;
      if (this._class !== Product._class) filter._class = this._class;
      const result = await collection.find(filter).toArray();
      return result.length;
    };
  }

  @Subscription(type => [Product])
  static subscribeForProductPicker(
    @Arg('institutionID', type => String) institutionID: string,
    @Arg('isOnlyWorkshops', type => Boolean) isOnlyWorkshops: boolean = false,
    @Arg('timeframeID', type => String) timeframeID: Maybe<string> = undefined,
  ) {
    return (gqlFields: BaseQLSelectionSet<Product>) => {
      return watchQuery(this._collectionName, {
        institutionID,
        ...(isOnlyWorkshops ? { _class: 'Workshop' } : {}),
        ...(timeframeID !== undefined ? { timeframeID } : {}),
      });
    };
  }

  static sortFunction = (a: Product, b: Product) => {
    if (a.sortingWeight === null && b.sortingWeight === null) return 0;
    if (a.sortingWeight === b.sortingWeight) return a.name.localeCompare(b.name);
    return (a.sortingWeight ?? 0) - (b.sortingWeight ?? 0);
  };
}
